export type EscrowStatus =
  | "PendingAdminApproval"
  | "ApprovedSentToEscrow"
  | "Active"
  | "Closed"
  | "Cancelled"
  | "Archived";

export interface EscrowTransactionDto {
  id: string;
  dealId: string;
  dealNumber: string | null;
  businessName: string | null;
  escrowComTransactionId: string | null;
  status: EscrowStatus;
  amount: number;
  currency: string;
  itemDescription: string;
  inspectionPeriodSeconds: number;
  draftedByUserId: string | null;
  approvedByUserId: string | null;
  draftedAt: string | null;
  approvedAt: string | null;
  terminalStateReachedAt: string | null;
  archivedAt: string | null;
  lastSyncedAt: string | null;
  createdAt: string;
  /** True when the server resolves to LIVE Escrow.com (real funds). Drives the
   * "LIVE — real funds" badge. Optional so pre-existing fixtures stay valid. */
  isLive?: boolean;
}

export interface EscrowTransactionEventDto {
  id: string;
  eventType: string;
  event: string;
  payloadJson: string;
  receivedAt: string;
}

export interface EscrowTransactionEditDto {
  id: string;
  editedByUserId: string;
  editedAt: string;
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
}

export interface EscrowLifecycleStep {
  reached: boolean;
  at: string | null;
}

export interface EscrowLifecycle {
  drafted: EscrowLifecycleStep;
  approved: EscrowLifecycleStep;
  sentToEscrow: EscrowLifecycleStep;
  buyerAgreed: EscrowLifecycleStep;
  buyerFunded: EscrowLifecycleStep;
  disbursed: EscrowLifecycleStep;
  closed: EscrowLifecycleStep;
}

export interface EscrowTransactionDetailDto extends EscrowTransactionDto {
  /** Resolved buyer email — Phoenix terminology: "Funds from". */
  buyerEmail: string | null;
  /** True when the drafter overrode the canonical deal email for this escrow. */
  buyerEmailOverridden: boolean;
  /** Canonical deal email snapshot at draft time — shown in the override banner. */
  canonicalBuyerEmailAtDraft: string | null;
  draftedByUserName: string | null;
  approvedByUserName: string | null;
  notes: string | null;
  rawEscrowPayloadJson: string | null;
  externalUrl: string | null;
  escrowComFeeAmount: number | null;
  escrowComFeeCurrency: string | null;
  lifecycle: EscrowLifecycle;
  events: EscrowTransactionEventDto[];
  edits: EscrowTransactionEditDto[];
}

export interface EscrowWebhookDiagnosticsDto {
  registeredUrl: string | null;
  expectedUrl: string;
  match: boolean;
  mostRecentEventReceivedAt: string | null;
}

/** Pre-draft preview of the resolved buyer ("Funds from") email for a deal. */
export interface EscrowBuyerEmailPreviewDto {
  buyerEmail: string | null;
}

export interface DraftEscrowRequest {
  dealId: string;
  amount: number;
  itemDescription: string;
  inspectionPeriodSeconds: number;
  /** Escrow-scoped "Funds from" override; omitted unless staff changed it. */
  buyerEmailOverride?: string;
}

export interface UpdateEscrowDraftRequest {
  amount: number;
  itemDescription: string;
  inspectionPeriodSeconds: number;
}

export const ESCROW_PERMISSIONS = {
  Read: "Escrow.Read",
  Draft: "Escrow.Draft",
  Approve: "Escrow.Approve",
} as const;

export const ESCROW_STATUS_LABEL: Record<EscrowStatus, string> = {
  PendingAdminApproval: "Pending approval",
  ApprovedSentToEscrow: "Sent to Escrow.com",
  Active: "Active",
  Closed: "Closed",
  Cancelled: "Cancelled",
  Archived: "Archived",
};

type BadgeStatus = "draft" | "review" | "finalized" | "archived" | "error" | "info" | "gold";

export function escrowStatusBadge(status: EscrowStatus): BadgeStatus {
  switch (status) {
    case "PendingAdminApproval":
      return "draft";
    case "ApprovedSentToEscrow":
    case "Active":
      return "info";
    case "Closed":
      return "finalized";
    case "Cancelled":
      return "error";
    case "Archived":
      return "archived";
  }
}

/** Inspection-period presets (seconds) for the create form. */
export const INSPECTION_PERIOD_OPTIONS: { label: string; seconds: number }[] = [
  { label: "1 day", seconds: 86_400 },
  { label: "3 days", seconds: 259_200 },
  { label: "7 days", seconds: 604_800 },
  { label: "14 days", seconds: 1_209_600 },
];
